/* Wishlist Hero の PDP 標準ボタンの状態補正を管理する */
(() => {
  if (window.AuremePdpWishlistButtonState) return;

  const createReadyRetryScheduler = window.AuremeReadyRetry?.createScheduler;
  if (typeof createReadyRetryScheduler !== 'function') {
    console.error('[aureme-pdp-wishlist-button-state] ready retry helper is not loaded.');
    return;
  }

  const PDP_WISHLIST_BUTTON_SELECTOR = '#wishlist-hero-product-page-button';
  const PDP_WISHLIST_BUTTON_CONTROL_SELECTOR = 'button, [role="button"]';
  const PDP_WISHLIST_BUTTON_FALLBACK_SELECTOR = '.product-form__buttons';
  const PDP_WISHLIST_INTERACTIVE_SELECTOR = `${PDP_WISHLIST_BUTTON_CONTROL_SELECTOR}, ${PDP_WISHLIST_BUTTON_FALLBACK_SELECTOR}`;
  const PDP_WISHLIST_ADDED_CLASS = 'wishlisthero-product-in-wishlist';
  const PDP_WISHLIST_ADDED_LABEL = 'お気に入り解除';
  const PDP_WISHLIST_DEFAULT_LABEL = 'お気に入り';
  const PDP_WISHLIST_EMPTY_BOOKMARK_CLASS = 'wlh-svg-icon-bookmark-empty';
  const PDP_WISHLIST_ICON_CLASS = 'wlh-svg-Icon';
  const PDP_WISHLIST_FILLED_BOOKMARK_VIEW_BOX = '0 0 384 512';
  const PDP_WISHLIST_FILLED_BOOKMARK_PATH =
    'M0 512V48C0 21.49 21.49 0 48 0h288c26.51 0 48 21.49 48 48v464L192 400 0 512z';
  const PDP_WISHLIST_EMPTY_BOOKMARK_VIEW_BOX = '0 0 23 32';
  const PDP_WISHLIST_EMPTY_BOOKMARK_PATH =
    'M20.576 4.576h-18.304v22.176l9.152-8.768 1.6 1.504 7.552 7.264v-22.176zM20.8 2.272q0.384 0 0.768 0.16 0.608 0.256 0.928 0.736t0.352 1.12v23.008q0 0.608-0.352 1.12t-0.928 0.736q-0.32 0.128-0.768 0.128-0.864 0-1.504-0.576l-7.872-7.552-7.872 7.552q-0.64 0.608-1.472 0.608-0.416 0-0.8-0.16-0.576-0.224-0.928-0.736t-0.352-1.12v-23.008q0-0.608 0.352-1.12t0.928-0.736q0.384-0.16 0.8-0.16h18.72z';
  const PDP_BUTTON_STATE_RETRY_INTERVAL_MS = 100;
  const PDP_BUTTON_STATE_MAX_RETRY_COUNT = 100;

  const observerRetryScheduler = createReadyRetryScheduler({
    intervalMs: PDP_BUTTON_STATE_RETRY_INTERVAL_MS,
    maxRetryCount: PDP_BUTTON_STATE_MAX_RETRY_COUNT,
  });

  let observedPdpButtonContainer = null;
  let pdpButtonStateObserver = null;

  function getContainer() {
    return document.querySelector(PDP_WISHLIST_BUTTON_SELECTOR);
  }

  function getContainerFromTarget(target) {
    if (!(target instanceof Element)) return null;

    return target.closest(PDP_WISHLIST_BUTTON_SELECTOR);
  }

  function getInteractiveElement(target, container) {
    if (!(target instanceof Element) || !container) return null;

    const clickedElement = target.closest(PDP_WISHLIST_INTERACTIVE_SELECTOR);
    return clickedElement && container.contains(clickedElement) ? clickedElement : null;
  }

  function getButton(container) {
    if (!container) return null;
    if (container.matches(PDP_WISHLIST_BUTTON_CONTROL_SELECTOR)) return container;

    return (
      container.querySelector(PDP_WISHLIST_BUTTON_CONTROL_SELECTOR) ||
      (container.matches(PDP_WISHLIST_BUTTON_FALLBACK_SELECTOR) ? container : null) ||
      container.querySelector(PDP_WISHLIST_BUTTON_FALLBACK_SELECTOR) ||
      null
    );
  }

  function isAdded(container) {
    const button = getButton(container);
    if (!button) return null;

    /*
      Wishlist Hero 標準ボタンの DOM は外部 SDK 生成のため、状態判定は
      SDK が付与するクラス名とラベル属性の両方をフォールバックとして見る。
    */
    const stateText = [
      button.getAttribute('aria-label'),
      button.getAttribute('title'),
      button.className,
    ]
      .join(' ')
      .toLowerCase();

    return (
      button.classList.contains(PDP_WISHLIST_ADDED_CLASS) ||
      stateText.includes('お気に入り解除') ||
      stateText.includes('remove') ||
      stateText.includes('in-wishlist')
    );
  }

  function resetBookmarkIconSize(icon) {
    icon.removeAttribute('width');
    icon.removeAttribute('height');
    icon.style.removeProperty('width');
    icon.style.removeProperty('height');
  }

  function applyFilledBookmarkIcon(button) {
    const icon = button.querySelector('svg');
    if (!icon) return;

    const [primaryPath, ...extraPaths] = Array.from(icon.querySelectorAll('path'));
    if (!primaryPath) return;

    /* Wishlist Hero のアイコン DOM と FontAwesome 定義に依存するため、SDK 更新時はここも回帰確認する */
    resetBookmarkIconSize(icon);
    icon.classList.remove(PDP_WISHLIST_EMPTY_BOOKMARK_CLASS);
    icon.classList.add(PDP_WISHLIST_ICON_CLASS);
    icon.setAttribute('data-prefix', 'fas');
    icon.setAttribute('data-icon', 'bookmark');
    icon.setAttribute('viewBox', PDP_WISHLIST_FILLED_BOOKMARK_VIEW_BOX);
    primaryPath.setAttribute('fill', 'currentColor');
    primaryPath.setAttribute('d', PDP_WISHLIST_FILLED_BOOKMARK_PATH);

    for (const path of extraPaths) {
      path.remove();
    }
  }

  function applyEmptyBookmarkIcon(button) {
    const icon = button.querySelector('svg');
    if (!icon) return;

    const [primaryPath, ...extraPaths] = Array.from(icon.querySelectorAll('path'));
    if (!primaryPath) return;

    /* Wishlist Hero のアイコン DOM と空ブックマーク定義に依存するため、SDK 更新時はここも回帰確認する */
    resetBookmarkIconSize(icon);
    icon.classList.add(PDP_WISHLIST_ICON_CLASS, PDP_WISHLIST_EMPTY_BOOKMARK_CLASS);
    icon.removeAttribute('data-prefix');
    icon.removeAttribute('data-icon');
    icon.setAttribute('viewBox', PDP_WISHLIST_EMPTY_BOOKMARK_VIEW_BOX);
    primaryPath.setAttribute('fill', 'currentColor');
    primaryPath.setAttribute('d', PDP_WISHLIST_EMPTY_BOOKMARK_PATH);

    for (const path of extraPaths) {
      path.remove();
    }
  }

  function applyAddedState(container) {
    const button = getButton(container);
    if (!button) return;

    /*
      ログイン復帰直後や SDK 再描画後は Wishlist Hero 側のアイコン属性だけが
      古い状態で残ることがあるため、確定済みの状態に合わせて表示を補正する。
      バリアント切替後の再描画や未登録状態への戻しは Wishlist Hero 本体に委ねる。
    */
    button.classList.add(PDP_WISHLIST_ADDED_CLASS);
    button.setAttribute('aria-label', PDP_WISHLIST_ADDED_LABEL);
    button.setAttribute('title', PDP_WISHLIST_ADDED_LABEL);
    applyFilledBookmarkIcon(button);
  }

  function applyRemovedState(container) {
    const button = getButton(container);
    if (!button) return;

    button.classList.remove(PDP_WISHLIST_ADDED_CLASS);
    button.setAttribute('aria-label', PDP_WISHLIST_DEFAULT_LABEL);
    button.setAttribute('title', PDP_WISHLIST_DEFAULT_LABEL);
    applyEmptyBookmarkIcon(button);
  }

  function applyState(container, isAddedState) {
    if (isAddedState) {
      applyAddedState(container);
      return;
    }

    applyRemovedState(container);
  }

  function isStateNormalized(container, isAddedState) {
    const button = getButton(container);
    const icon = button?.querySelector('svg');
    const primaryPath = icon?.querySelector('path');
    if (!button || !icon || !primaryPath) return true;

    if (isAddedState) {
      return (
        button.classList.contains(PDP_WISHLIST_ADDED_CLASS) &&
        button.getAttribute('aria-label') === PDP_WISHLIST_ADDED_LABEL &&
        button.getAttribute('title') === PDP_WISHLIST_ADDED_LABEL &&
        icon.classList.contains(PDP_WISHLIST_ICON_CLASS) &&
        !icon.classList.contains(PDP_WISHLIST_EMPTY_BOOKMARK_CLASS) &&
        icon.getAttribute('viewBox') === PDP_WISHLIST_FILLED_BOOKMARK_VIEW_BOX &&
        primaryPath.getAttribute('fill') === 'currentColor' &&
        primaryPath.getAttribute('d') === PDP_WISHLIST_FILLED_BOOKMARK_PATH
      );
    }

    return (
      !button.classList.contains(PDP_WISHLIST_ADDED_CLASS) &&
      button.getAttribute('aria-label') === PDP_WISHLIST_DEFAULT_LABEL &&
      button.getAttribute('title') === PDP_WISHLIST_DEFAULT_LABEL &&
      icon.classList.contains(PDP_WISHLIST_ICON_CLASS) &&
      icon.classList.contains(PDP_WISHLIST_EMPTY_BOOKMARK_CLASS) &&
      icon.getAttribute('viewBox') === PDP_WISHLIST_EMPTY_BOOKMARK_VIEW_BOX &&
      primaryPath.getAttribute('fill') === 'currentColor' &&
      primaryPath.getAttribute('d') === PDP_WISHLIST_EMPTY_BOOKMARK_PATH
    );
  }

  function normalizeIfNeeded(container, isAddedState) {
    if (isStateNormalized(container, isAddedState)) return;

    applyState(container, isAddedState);
  }

  function normalizeCurrentState() {
    const container = getContainer();
    const currentIsAdded = isAdded(container);
    if (currentIsAdded === null) return false;

    normalizeIfNeeded(container, currentIsAdded);
    return true;
  }

  function observe(container) {
    if (!container || observedPdpButtonContainer === container) return Boolean(container);

    pdpButtonStateObserver?.disconnect();
    pdpButtonStateObserver = new MutationObserver(() => {
      normalizeCurrentState();
    });
    pdpButtonStateObserver.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    observedPdpButtonContainer = container;
    normalizeCurrentState();
    return true;
  }

  function scheduleObserver(retryCount = 0) {
    observerRetryScheduler.schedule({
      tryReady: () => observe(getContainer()),
      onRetry: scheduleObserver,
      retryCount,
    });
  }

  function scheduleStateChecks() {
    /*
      バリアント切替時は Wishlist Hero がボタン DOM を非同期に再描画するため、
      直後と少し遅れたタイミングの両方でアイコン属性を補正する。
    */
    const checkState = () => {
      scheduleObserver();
      normalizeCurrentState();
    };

    if (typeof window.queueMicrotask === 'function') {
      window.queueMicrotask(checkState);
    } else {
      window.setTimeout(checkState, 0);
    }

    window.requestAnimationFrame?.(checkState);
    window.setTimeout(checkState, 50);
    window.setTimeout(checkState, 250);
  }

  window.AuremePdpWishlistButtonState = {
    applyAddedState,
    applyRemovedState,
    getButton,
    getContainer,
    getContainerFromTarget,
    getInteractiveElement,
    isAdded,
    normalizeIfNeeded,
    scheduleObserver,
    scheduleStateChecks,
  };
})();
